import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";

const faqs = [
  {
    question: "Why is dental care important for pets?",
    answer:
      "Dental care keeps your pet's teeth and gums healthy, and helps prevent pain, infection and other health issues from building up over time.",
  },
  {
    question: "What are the signs of dental disease in pets?",
    answer:
      "Bad breath, yellow tartar, swollen gums, and trouble chewing are common early signs. If you notice these, it's worth booking a checkup soon.",
  },
  {
    question: "How often should my pet have a dental check-up?",
    answer:
      "Most vets recommend a dental checkup at least once a year, though pets prone to dental issues may need more frequent visits.",
  },
  {
    question: "Do you provide professional teeth cleaning for pets?",
    answer:
      "Yes. The clinics listed on PetQura offer professional teeth cleaning as part of their pet dental treatment services.",
  },
  {
    question: "Can poor dental health affect my pet's overall wellbeing?",
    answer:
      "Yes. Untreated dental problems can lead to infections that affect other organs, along with pain that makes eating and daily activity difficult.",
  },
  {
    question: "How do I find pet dental care near me using PetQura?",
    answer:
      "Simply enter your location and select the dental service you need. PetQura will show you nearby clinics along with their available appointment slots.",
  },
  {
    question: "Is anesthesia always required for pet dental cleaning?",
    answer:
      "Not always. It depends on your pet's temperament and how extensive the cleaning needs to be. Your vet will recommend the safest option.",
  },
  {
    question: "How much does pet dental cleaning cost?",
    answer:
      "Pet dental cleaning cost varies by clinic and depends on your pet's specific needs. You can compare pricing across clinics listed on PetQura before booking.",
  },
  {
    question: "Can I book a same-day dental appointment through PetQura?",
    answer:
      "This depends on clinic availability. Many clinics on PetQura list real-time slots, so same-day appointments are often possible.",
  },
  {
    question: "What should I do to prepare my pet for a dental visit?",
    answer:
      "Keep your pet calm before the visit, avoid feeding them right before if anesthesia might be needed, and bring along any past dental records if you have them.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetDentalCare = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Dental Care Near You in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Looking for pet dental care near you? Find trusted clinics for pet dental cleaning, check pet dental treatment options and compare pet dental cleaning cost across Bangalore."
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Dental Care and Cleaning in Bangalore With PetQura</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <p>
            At PetQura, we believe a healthy pet starts with a healthy mouth.
            If you've ever searched for <em>pet dental care near me</em> late
            at night while your dog turned away from their food, you already
            know how confusing it can be to find the right clinic quickly.
            Dental health is one of the most overlooked parts of pet care, yet
            it affects everything from your pet's appetite to their energy and
            long-term wellbeing. We connect pet parents across Bangalore with
            veterinary clinics that offer proper dental care, so you don't have
            to spend hours calling around or scrolling through reviews. Just
            tell us what your pet needs, and we'll show you clinics nearby that
            can help.
          </p>
        </HeroSection>

        {/* ── Pet Dental Treatment ── */}
        <ContentSection>
          <SectionHeading>Pet Dental Treatment Near You</SectionHeading>
          <p>
            Every pet's mouth is different, and so are their dental needs.
            That's why the clinics listed on PetQura offer a range of pet
            dental treatment options suited to your pet's age, breed and health
            history. Whether it's a first visit or a routine follow-up, you can
            expect care that's thorough, gentle and unhurried.
          </p>
          <p>Here's what this treatment usually includes:</p>
          <BulletList>
            <li>Oral health checkups to catch problems early</li>
            <li>
              Professional teeth cleaning to remove plaque and tartar
            </li>
            <li>Treatment for gum disease and inflammation</li>
            <li>Tooth extractions, when a tooth is too damaged to save</li>
            <li>
              Guidance on at home pet dental care between visits
            </li>
          </BulletList>
          <p>
            Finding pet dental care near you shouldn't mean settling for the
            first clinic that pops up online. With PetQura, you can filter by
            service, check clinic details, and pick a time slot that works for
            you, all before you even leave the house.
          </p>
        </ContentSection>

        {/* ── Importance of Dental Care ── */}
        <ContentSection>
          <SectionHeading>Importance Of Dental Care For Pets</SectionHeading>
          <p>
            Skipping dental care doesn't just lead to bad breath. Left
            untreated, plaque and tartar build up along the gum line, causing
            inflammation, pain and eventually infection. Some pets stop eating
            properly because chewing hurts. Others end up with tooth loss, or
            infections that spread further and affect the heart, liver or
            kidneys. What starts as a small issue can quietly turn into a much
            bigger, more painful, and more expensive problem.
          </p>
          <p>
            That's the main reason we encourage every pet parent to treat
            dental care the same way they treat vaccinations: not optional, but
            essential.
          </p>
          <p>Common signs your pet may need dental care:</p>
          <BulletList>
            <li>Bad breath that doesn't go away</li>
            <li>Yellow or brown buildup on the teeth</li>
            <li>Bleeding or swollen gums</li>
            <li>Difficulty chewing, or dropping food while eating</li>
            <li>Pawing at the mouth or face</li>
          </BulletList>
          <p>
            If you've noticed any of these signs, don't wait it out. You can
            book an appointment for pet dental care near you through the PetQura
            website in just a few clicks, pick a clinic near you, choose a slot,
            and get your pet the care they need without the back-and-forth of
            phone calls.
          </p>
        </ContentSection>

        {/* ── Cost ── */}
        <ContentSection>
          <SectionHeading>Pet Dental Treatment Cost</SectionHeading>
          <p>
            We know cost is one of the first things pet parents want clarity on
            before booking an appointment. Pet dental treatment cost in
            Bangalore isn't fixed, and it shouldn't be treated as a
            one-size-fits-all number. It depends on a few factors specific to
            your pet and the clinic you choose.
          </p>
          <p>Here's what usually affects the cost:</p>
          <BulletList>
            <li>Your pet's size and breed</li>
            <li>How severe the plaque or tartar buildup is</li>
            <li>Whether anesthesia is required for the procedure</li>
            <li>Any additional treatment, such as extractions</li>
            <li>The clinic's location and facilities</li>
          </BulletList>
          <p>
            On PetQura, clinic listings are upfront about the services they
            offer, so you can compare your options and choose a clinic that fits
            your pet's needs and your budget, without any guesswork.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetDentalCare;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
